import { Guid } from 'js-guid';
import { CONTENT_ITEM_KEYS } from '../../features/CreateContent/constants.js';

const isFile = (value) => typeof Blob !== 'undefined' && value instanceof Blob;

/**
 * Блоки контента → модель для API.
 * Новый файл уходит в форму полем с Guid блока (у картинок слайдера — свой Guid),
 * строка — уже загруженный путь: бэкенд сохранит его по Guid.
 */
export const serializeContentItems = (items = [], formData) =>
  items.map((item) => {
    const { guid, discriminator } = item;

    if (discriminator === 'TextContentItem') {
      return { guid, discriminator, content: item.content ?? '' };
    }

    if (discriminator === 'ImageSliderContentItem') {
      const images = (item.images || []).map((img) => {
        if (isFile(img.imgPath)) {
          const imageGuid = Guid.newGuid().toString();
          formData.append(imageGuid, img.imgPath);
          return { guid: imageGuid, discriminator: 'ImageContentItem', imgPath: '' };
        }
        return { guid: img.guid, discriminator: 'ImageContentItem', imgPath: img.imgPath || '' };
      });
      return { guid, discriminator, images };
    }

    const key = CONTENT_ITEM_KEYS[discriminator];
    const value = item[key];
    if (isFile(value)) {
      formData.append(guid, value);
      return { guid, discriminator, [key]: '' };
    }
    return { guid, discriminator, [key]: value || '' };
  });

const withoutPrivateFields = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([key]) => !key.startsWith('_')));

/**
 * Собирает multipart-форму для Create/Update сущности с вложенными локализациями.
 *
 * @param {object} params
 * @param {object} params.model — основные поля сущности (JSON-модель)
 * @param {File|Blob|null} [params.mainImage] — новая основная картинка
 * @param {string} [params.mainImageField='mainImg'] — имя поля формы основной картинки
 * @param {string[]} [params.contentKeys=['content']] — поля с контентом ({ items: [...] }) в модели и локализациях
 * @param {Array|null} [params.localizations] — локализации из LocalizationsBlock (null — не отправлять)
 * @param {string} [params.localizationImageField='mainImg'] — суффикс поля картинки локализации: localization_{LANG}_{field}
 * @param {Array<{name: string, formField: string}>} [params.galleryFields=[]] — галереи ([{ key, imgPath } | { key, file }]
 *   из GalleryEditor) в модели и локализациях: в JSON уходит итоговый список строк — imgPath существующего фото
 *   или имя поля формы нового файла: {formField}_{guid} у модели, localization_{LANG}_{formField}_{guid} у локализации.
 *   Если галереи нет в объекте (undefined/null) — поле не трогается
 */
export const buildEntityFormData = ({
  model,
  mainImage = null,
  mainImageField = 'mainImg',
  contentKeys = ['content'],
  localizations = null,
  localizationImageField = 'mainImg',
  galleryFields = [],
}) => {
  const formData = new FormData();

  const serializeGalleries = (source, fieldPrefix) => {
    const result = { ...source };
    galleryFields.forEach(({ name, formField }) => {
      if (!Array.isArray(source[name])) return;
      result[name] = source[name]
        .map((item) => {
          if (isFile(item?.file)) {
            const fieldName = `${fieldPrefix}${formField}_${Guid.newGuid().toString()}`;
            formData.append(fieldName, item.file);
            return fieldName;
          }
          return typeof item === 'string' ? item : item?.imgPath;
        })
        .filter(Boolean);
    });
    return result;
  };

  const serializeContents = (source) => {
    const result = { ...source };
    contentKeys.forEach((key) => {
      if (source[key]) {
        result[key] = { ...source[key], items: serializeContentItems(source[key].items, formData) };
      }
    });
    return result;
  };

  const bodyModel = serializeGalleries(serializeContents(model), '');

  if (localizations) {
    bodyModel.localizations = localizations.map((localization) => {
      if (isFile(localization._imageFile)) {
        formData.append(`localization_${localization.language}_${localizationImageField}`, localization._imageFile);
      }
      // Несколько файлов у локализации (поля LocalizationsBlock с formField): { [formField]: File }
      Object.entries(localization._files || {}).forEach(([formField, file]) => {
        if (isFile(file)) {
          formData.append(`localization_${localization.language}_${formField}`, file);
        }
      });
      return withoutPrivateFields(serializeGalleries(serializeContents(localization), `localization_${localization.language}_`));
    });
  }

  if (mainImage) {
    formData.append(mainImageField, mainImage);
  }

  formData.append('model', JSON.stringify(bodyModel));
  return formData;
};

/**
 * Локализации из ответа API → значение для LocalizationsBlock
 */
export const mapLocalizationsFromApi = (localizations = [], contentKeys = ['content']) =>
  (localizations || []).map((localization) => {
    const result = { ...localization, language: String(localization.language || '').toUpperCase() };
    contentKeys.forEach((key) => {
      if (localization[key]) {
        result[key] = { items: localization[key].items || [] };
      }
    });
    return result;
  });
